import { fetchPdfBytes } from './pdfDocumentBytes';

/** Spätestens dann iframe und Object-URL aufräumen, falls `afterprint` nie kommt. */
const PRINT_CLEANUP_FALLBACK_MS = 60_000;

/**
 * Safari (macOS) und alle iOS-/iPadOS-Browser drucken PDFs aus einem iframe unzuverlässig
 * (leere Seite oder die App statt des PDFs). Dort das PDF in einem neuen Tab öffnen.
 * iPadOS meldet sich als Desktop-Safari („Macintosh“), erkennbar an Touch-Unterstützung.
 */
export function shouldOpenPdfForPrint(userAgent: string, maxTouchPoints = 0): boolean {
  const ua = userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  if (/Macintosh/i.test(ua) && maxTouchPoints > 1) return true;
  const isSafari = /Safari\//.test(ua) && !/Chrome\/|Chromium\/|CriOS\/|Edg\/|OPR\/|Firefox\/|FxiOS\//.test(ua);
  return isSafari;
}

function openInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener');
}

/**
 * Druckt ein PDF über den Druckdialog des Browsers. Die Bytes kommen über denselben Weg wie in
 * der Vorschau (`fetchPdfBytes`) und laufen als Blob in einen unsichtbaren iframe — so klappt es
 * auch bei signierten Storage-Links anderer Domains, auf deren iframe `print()` blockiert wäre.
 */
export async function printPdfFromUrl(url: string): Promise<void> {
  if (shouldOpenPdfForPrint(navigator.userAgent, navigator.maxTouchPoints)) {
    openInNewTab(url);
    return;
  }

  const bytes = await fetchPdfBytes(url);
  const blobUrl = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
  const iframe = document.createElement('iframe');
  // Nicht display:none — dann druckt Chrome eine leere Seite.
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    iframe.remove();
    URL.revokeObjectURL(blobUrl);
  };
  const fallbackTimer = window.setTimeout(cleanup, PRINT_CLEANUP_FALLBACK_MS);

  await new Promise<void>((resolve) => {
    iframe.onload = () => {
      const win = iframe.contentWindow;
      try {
        if (!win) throw new Error('no print window');
        win.addEventListener('afterprint', () => {
          window.clearTimeout(fallbackTimer);
          // Kurz warten: manche Browser feuern afterprint, bevor der Druckauftrag übergeben ist.
          window.setTimeout(cleanup, 1_000);
        });
        win.focus();
        win.print();
      } catch {
        window.clearTimeout(fallbackTimer);
        cleanup();
        openInNewTab(url);
      }
      resolve();
    };
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
  });
}
